import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  IoHardwareChipOutline, 
  IoSparklesOutline, 
  IoTimeOutline, 
  IoCreateOutline,
  IoBulbOutline,
  IoCheckmarkCircleOutline,
  IoShieldCheckmarkOutline,
  IoLanguageOutline,
  IoRefreshOutline,
  IoTrophyOutline,
  IoAddOutline
} from 'react-icons/io5';
import './GenerateQuestion.css';
import { API_URL } from '../utils/networkConfig';

const GenerateQuestion = () => {
  const [question, setQuestion] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [topic, setTopic] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleAIGenerate = async () => {
    if (!topic.trim()) {
      setError('Please enter a topic for AI generation.');
      return;
    }
    
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        setError('Please log in to generate questions.');
        return;
      }

      const res = await fetch(`${API_URL}/api/game-sessions/ai-generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ topic: topic.trim() }),
      });
      
      const data = await res.json();
      
      if (res.ok) {
        setQuestion(data.question);
        setSuccess('AI question generated successfully!');
      } else {
        setError(data.error || 'Failed to generate AI question.');
      }
    } catch (err) {
      console.error('AI Generation error:', err);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!question.trim()) {
      setError('Please enter a question.');
      return;
    }
    
    if (!startTime || !endTime) {
      setError('Please select both start and end times.');
      return;
    }
    
    const startDateTime = new Date(startTime);
    const endDateTime = new Date(endTime);
    
    if (endDateTime <= startDateTime) {
      setError('End time must be after start time.');
      return;
    }
    
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        setError('Please log in to create questions.');
        return;
      }

      const res = await fetch(`${API_URL}/api/game-sessions/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          question: question.trim(),
          startTime,
          endTime
        }),
      });
      
      const data = await res.json();
      
      if (res.ok) {
        setSuccess('Question created successfully!');
        setQuestion('');
        setTopic('');
        setStartTime('');
        setEndTime('');
        
        setTimeout(() => {
          navigate('/admin');
        }, 2000);
      } else {
        setError(data.error || 'Failed to create question.');
      }
    } catch (err) {
      console.error('Question creation error:', err);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="generate-question-container">
      <div className="page-header">
        <h2><IoCreateOutline className="header-icon" />Generate New Question</h2>
        <p className="info-text">
          Create a question for players to answer freely. Points will be awarded based on matching answers - 
          if 3 people give the same answer, each gets 3 points!
        </p>
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      <div className="ai-section">
        <div className="ai-header">
          <IoHardwareChipOutline className="ai-icon" />
          <h3>AI Question Generation</h3>
        </div>
        <div className="ai-form">
          <input
            type="text"
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            placeholder="Enter topic (e.g., science, history, movies, food)..."
            className="topic-input"
          />
          <button 
            type="button" 
            onClick={handleAIGenerate}
            disabled={loading}
            className="ai-generate-button"
          >
            {loading ? (
              <>
                <IoRefreshOutline className="spinning" />
                Generating...
              </>
            ) : (
              <>
                <IoSparklesOutline />
                Generate AI Question
              </>
            )}
          </button>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="generate-question-form">
        <div className="form-group">
          <label><IoCreateOutline className="label-icon" />Question:</label>
          <textarea
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder="Enter your question here or use AI generation above..."
            rows="3"
            required
          />
        </div>

        <div className="time-section">
          <div className="form-group">
            <label><IoTimeOutline className="label-icon" />Start Time:</label>
            <input
              type="datetime-local"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              required
            />
          </div>
          <div className="form-group">
            <label><IoTimeOutline className="label-icon" />End Time:</label>
            <input
              type="datetime-local"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              required
            />
          </div>
        </div>

        <button type="submit" className="generate-button">
          <IoTrophyOutline />
          Create Question Session
        </button>
      </form>

      <div className="how-it-works">
        <div className="section-header">
          <IoHardwareChipOutline className="section-icon" />
          <h3>AI-Powered Answer Matching System</h3>
        </div>
        <div className="features-grid">
          <div className="feature-card">
            <IoCheckmarkCircleOutline className="feature-icon" />
            <h4>Smart Grouping</h4>
            <p>AI automatically groups similar answers together</p>
          </div>
          <div className="feature-card">
            <IoLanguageOutline className="feature-icon" />
            <h4>Handles Misspellings</h4>
            <p>"bleu" and "blue" count as the same answer</p>
          </div>
          <div className="feature-card">
            <IoShieldCheckmarkOutline className="feature-icon" />
            <h4>Recognizes Synonyms</h4>
            <p>"car" and "automobile" are grouped together</p>
          </div>
          <div className="feature-card">
            <IoSparklesOutline className="feature-icon" />
            <h4>Abbreviations</h4>
            <p>"USA" and "United States of America" match</p>
          </div>
          <div className="feature-card">
            <IoRefreshOutline className="feature-icon" />
            <h4>Different Forms</h4>
            <p>"run" and "running" are considered equivalent</p>
          </div>
          <div className="feature-card">
            <IoTrophyOutline className="feature-icon" />
            <h4>Fair Scoring</h4>
            <p>More similar answers = more points for everyone in that group</p>
          </div>
        </div>
        <div className="ai-note">
          <IoBulbOutline className="note-icon" />
          <p>The system automatically falls back to simple text matching if AI is unavailable, ensuring reliability.</p>
        </div>
      </div>
    </div>
  );
};

export default GenerateQuestion; 